import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Text, Link } from '@resideo/blueprint-react';
import styled from 'styled-components';
import {
  ALARMNET_360_URL,
  FIRST_ALERT_URL,
  HONEYWELL_HOME_URL,
  PRO_PERKS_URL,
  PRO_PORTAL_LINK_URL,
  RESIDEO_ACADEMY_LINK_URL,
} from 'config';
interface AccountModalWrapperProps {
  onClose: () => void;
}
interface AccountTypeModalProps {
  onClose: () => void;
  accountType?: 'personal' | 'business';
}

const ContentContainer = styled(Flex)`
  flex-direction: column;
  text-align: left;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  margin: 4px 0;

  &:hover {
    text-decoration: underline;
  }
`;

const CloseButtonContainer = styled(Flex)`
  justify-content: flex-end;
  margin-top: 20px;
`;

export const AccountTypeModal = ({
  onClose,
  accountType = 'personal',
}: AccountTypeModalProps) => {
  const { t } = useTranslation();

  const getContent = () => {
    if (accountType === 'business') {
      return {
        message: t('user.signUp.accountTypeModal.businessAccount.message'),
        description: t(
          'user.signUp.accountTypeModal.businessAccount.description'
        ),
        links: [
          { href: PRO_PORTAL_LINK_URL, text: 'Pro Portal' },
          { href: RESIDEO_ACADEMY_LINK_URL, text: 'Resideo Academy' },
          { href: ALARMNET_360_URL, text: 'AlarmNet 360' },
          { href: PRO_PERKS_URL, text: 'Pro PERKS' },
        ],
        finalMessage: t(
          'user.signUp.accountTypeModal.businessAccount.finalMessage'
        ),
        testId: 'business-account-modal-close',
      };
    }

    return {
      message: t('user.signUp.accountTypeModal.personalAccount.message'),
      description: t(
        'user.signUp.accountTypeModal.personalAccount.description'
      ),
      links: [
        { href: FIRST_ALERT_URL, text: 'www.firstalert.com' },
        { href: HONEYWELL_HOME_URL, text: 'www.honeywellhome.com' },
      ],
      finalMessage: t(
        'user.signUp.accountTypeModal.personalAccount.finalMessage'
      ),
      testId: 'personal-account-modal-close',
    };
  };

  const content = getContent();

  return (
    <>
      <ContentContainer>
        <Text marginBottom='medium' fontSize='medium'>
          {content.message}
        </Text>

        <Text marginBottom='medium' fontSize='medium'>
          {content.description}
        </Text>

        <Flex flexDirection='column' marginBottom='medium'>
          {content.links.map(link => (
            <StyledLink
              key={link.href}
              href={link.href}
              target='_blank'
              rel='noopener noreferrer'>
              {link.text}
            </StyledLink>
          ))}
        </Flex>

        <Text marginBottom='large' fontSize='medium'>
          {content.finalMessage}
        </Text>

        <CloseButtonContainer>
          <Button
            variant='primary'
            onClick={onClose}
            data-test={content.testId}>
            {t('common.close', 'Close')}
          </Button>
        </CloseButtonContainer>
      </ContentContainer>
    </>
  );
};

export const PersonalAccountModal = (props: AccountModalWrapperProps) => (
  <AccountTypeModal {...props} accountType='personal' />
);

export const BusinessAccountModal = (props: AccountModalWrapperProps) => (
  <AccountTypeModal {...props} accountType='business' />
);

export default AccountTypeModal;
